import { CommunicationRules } from '@quromedical/business-rules';
import { MessageType, type TemplateNames } from '@quromedical/communication';
import type {
  ConversationMessage,
  ConversationSendMessageRequest,
} from '@quromedical/communication/server';
import {
  type ConversationContent,
  conversationContentSchema,
} from '@quromedical/models';
import { CrudForm, type SubmissionHandler } from 'components/form';
import type { Field } from 'components/types';
import { noOp } from 'helpers';
import type React from 'react';
import { useCallback, useState } from 'react';

import type { MessageTemplateFetcher } from './chatBottomSheet';

interface ChatFormProps {
  onSubmit: (data: ConversationSendMessageRequest) => void;
  patientId: string;
  nextOfKinId?: string;
  onAttachmentSubmit?: () => void;
  conversationHistory: ConversationMessage[];
  templateMessage?: MessageTemplateFetcher;
  multiLine: boolean;
}

export const isDisabled = (conversationHistory: ConversationMessage[]) => {
  return (
    CommunicationRules.WhatsappBusinessRules.validateCanSendWhatsappMessage({
      patientConversations: conversationHistory,
    }).type === 'error'
  );
};

export const createFields = (
  isDisabled: boolean,
  multiLine: boolean,
): Field<ConversationContent>[] => [
  {
    subfields: [
      {
        type: 'text-box',
        //TODO find the right number of lines
        numberOfLines: multiLine ? 5 : 1,
        key: 'content',
        label: '',
        placeholder: 'Message',
        multiline: true,
        autoResizeTextBox: multiLine ? false : true,
        isDisabled,
      },
    ],
  },
];

export const ChatForm: React.FC<ChatFormProps> = ({
  onSubmit,
  patientId,
  nextOfKinId,
  conversationHistory,
  onAttachmentSubmit = noOp,
  templateMessage,
  multiLine,
}) => {
  const [formState, setFormState] = useState(Date.now());

  const handleSubmit = useCallback<SubmissionHandler<ConversationContent>>(
    (data: ConversationContent) => {
      const toSend: ConversationSendMessageRequest = templateMessage
        ? {
            template: templateMessage.name as TemplateNames,
            patient: patientId,
            nextOfKin: nextOfKinId,
          }
        : {
            message: { type: MessageType.Text, text: data.content },
            patient: patientId,
            nextOfKin: nextOfKinId,
          };

      onSubmit(toSend);
      setFormState(Date.now());
      return {};
    },
    [templateMessage, onSubmit, patientId, nextOfKinId],
  );

  const handleAttachmentSubmit = useCallback(() => {
    onAttachmentSubmit();
    return {};
  }, [onAttachmentSubmit]);

  const disabled =
    isDisabled(conversationHistory) || templateMessage !== undefined;
  const fields = createFields(disabled, multiLine);
  const showTemplate = templateMessage
    ? { content: templateMessage.content }
    : {};
  const key = templateMessage ? `${templateMessage.name}` : formState;

  return (
    <CrudForm
      key={key}
      onSubmit={handleSubmit}
      fields={fields}
      initialValues={showTemplate}
      validationSchema={conversationContentSchema}
      buttonText="Send"
      buttonIcon="send"
      displayForm={true}
      cardProps={{
        paddingVertical: 0,
        borderColor: 'text-disabled',
        border: true,
      }}
      showSubmitButton
      onSecondarySubmit={handleAttachmentSubmit}
      secondaryButtonIcon="add"
      showSecondaryButtonIcon
    />
  );
};
